import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';

/**
 * NotificationListener — mounted once in App.js.
 * Polls the backend for new notifications and pops a react-toastify toast
 * whenever a brand-new unread notification arrives.
 */
const NotificationListener = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { newNotifications } = useNotifications();
  const shownIds = useRef(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || !newNotifications?.length) return;

    newNotifications.forEach((notif) => {
      if (shownIds.current.has(notif.id)) return;
      shownIds.current.add(notif.id);

      const icon = getIcon(notif.type);
      const toastType = getToastType(notif.type);
      const clickUrl = notif.type === 'new_ride_available'
        ? '/driver/dashboard#available-rides'
        : null;

      toast[toastType](
        <div
          onClick={clickUrl ? () => navigate(clickUrl) : undefined}
          className={clickUrl ? 'cursor-pointer' : ''}
        >
          <p className="font-semibold text-sm">{icon} {notif.title}</p>
          <p className="text-xs mt-0.5 opacity-90">{notif.message}</p>
          {clickUrl && <p className="text-xs mt-1 underline opacity-75">Tap to view available rides →</p>}
        </div>,
        {
          autoClose: 6000,
          closeOnClick: true,
        }
      );
    });
  }, [newNotifications, isAuthenticated, navigate]);

  return null;
};

function getIcon(type) {
  switch (type) {
    case 'ride_request':        return '🚗';
    case 'ride_assigned':       return '✅';
    case 'new_ride_available':  return '🔔';
    case 'on_the_way':          return '🚕';
    case 'driver_arrived':      return '📍';
    case 'ride_started':        return '🏁';
    case 'ride_completed':      return '✔️';
    case 'ride_cancelled':      return '❌';
    case 'verification_status': return '📋';
    case 'admin_alert':         return '⚠️';
    default:                    return '🔔';
  }
}

function getToastType(type) {
  switch (type) {
    case 'ride_completed':
    case 'ride_assigned':
    case 'new_ride_available':  return 'success';
    case 'ride_cancelled':      return 'error';
    case 'admin_alert':         return 'warning';
    case 'verification_status': return 'info';
    default:                    return 'info';
  }
}

export default NotificationListener;
